/**
 * Read the operational execution recorded on an incident (incident.rca items of
 * the operational types) and derive the operator-facing recovery status. Purely
 * generic: whatever the incident is about, recovery is judged by what the
 * execution reported — success, validation, a person needed — never by
 * recognising named incidents, files or transports.
 */
import type { AmsIncident } from '../services/api';
import { displayIncidentSummary, displayIncidentTitle } from './mimeText';
import { parseIncidentEmail } from './incidentInvestigation';

export interface OpsArtifactRecovery {
  kind?: string;
  expected_path?: string;
  absolute_path?: string;
  source?: string;
  source_path?: string;
  recovered?: boolean;
  generated?: boolean;
}

export interface OpsMissionReview {
  recovery_strategy?: string;
  files_recovered?: string[];
  files_generated?: string[];
  validation_evidence?: string[];
  final_operational_outcome?: string;
  artifact?: OpsArtifactRecovery;
  diagnosis?: string;
  episode_id?: string;
}

/** A recorded plan stage (older execution records carry these). */
export interface OpsPlanStage {
  id?: string;
  kind?: string;
  title?: string;
  detail?: string;
  status?: string;
}

export interface OpsAttempt {
  strategy?: string;
  diagnosis?: string;
  success?: boolean;
  validated?: boolean;
  actions?: string[];
  evidence?: string[];
}

export interface OpsRunReport {
  incident_id?: string;
  diagnosis?: string;
  diagnosis_evidence?: string[];
  attempts?: OpsAttempt[];
  artifact?: OpsArtifactRecovery;
  mission_review?: OpsMissionReview;
  success?: boolean;
  validated?: boolean;
  requires_engineering?: boolean;
  requires_human_approval?: boolean;
  policy_rejected?: boolean;
  lab_demonstration?: boolean;
  diagnosis_confidence?: number;
  selected_strategies?: string[];
  summary?: string;
  episode_id?: string;
  started_at?: string;
  completed_at?: string;
}

export interface ExactChange {
  kind: 'copy' | 'generate' | 'restore' | 'action' | 'sync';
  label: string;
  detail: string;
}

export interface ExtractedAlert {
  title: string;
  signal: string;
  body: string;
  from?: string;
  receivedAt?: string;
  source: string;
}

export interface ParsedOperationalRemediation {
  found: boolean;
  success: boolean;
  /** True when a validation/verify phase reported pass (stronger than success alone). */
  validated: boolean;
  summary: string;
  report: OpsRunReport | null;
  review: OpsMissionReview | null;
  resolution?: string;
  episodeId?: string;
  closedAt?: string | null;
  steps: { label: string; detail: string; status: 'ok' | 'warn' | 'pending' | 'fail' }[];
  exactChanges: ExactChange[];
  validationChecks: { label: string; pass: boolean }[];
}

/** Operator-facing recovery verdict derived from the recorded execution. */
export type OperationalRecoveryStatus =
  | 'idle'
  | 'running'
  | 'verified'
  | 'failed'
  | 'needs_human'
  | 'awaiting_upstream'
  | 'concluded_unresolved'
  | 'closed';

export interface OperationalRecoveryAssessment {
  status: OperationalRecoveryStatus;
  /** Short line for banners / header cues. */
  message: string;
  remediation: ParsedOperationalRemediation;
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

function asString(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() ? v.trim() : undefined;
}

/** The alert as the operator sees it: its own subject, body, sender and time. */
export function extractAlertFromIncident(inc?: AmsIncident | null): ExtractedAlert | null {
  if (!inc) return null;
  const email = parseIncidentEmail(inc);
  const title = displayIncidentTitle(email?.subject || inc.title);
  const body = displayIncidentSummary(email?.body || inc.summary || '');
  return {
    title,
    signal: title,
    body: body || title,
    from: email?.from,
    receivedAt: email?.receivedAt || inc.created_at,
    source: inc.source || 'email',
  };
}

const EMPTY: ParsedOperationalRemediation = {
  found: false,
  success: false,
  validated: false,
  summary: '',
  report: null,
  review: null,
  steps: [],
  exactChanges: [],
  validationChecks: [],
};

const OPERATIONAL_TYPES = new Set(['operational_remediation', 'operational_execution', 'operational_reliability']);

export function extractOperationalRemediation(inc?: AmsIncident | null): ParsedOperationalRemediation {
  if (!inc) return EMPTY;
  const resolution = inc.resolution || inc.operational_record?.resolution || inc.mission?.resolution;
  const closedAt = inc.closed_at || inc.operational_record?.closed_at || inc.mission?.closed_at;
  const items = (inc.rca ?? []).filter((r) => OPERATIONAL_TYPES.has((asString(r.type) || '').toLowerCase()));
  if (!items.length) return { ...EMPTY, resolution, closedAt };

  // The latest recorded execution is the current state of recovery.
  const item = items[items.length - 1];
  const raw = asRecord(item.report);
  const report = ((asRecord(raw?.remediation) || raw) as OpsRunReport | null) ?? null;
  const review = (asRecord(item.mission_review) || asRecord(report?.mission_review)) as OpsMissionReview | null;
  const success = Boolean(item.success) || Boolean(report?.success);
  const validated = Boolean(report?.validated);
  const summary =
    asString(item.detail) || asString(item.summary) || report?.summary || review?.final_operational_outcome || '';
  const episodeId = report?.episode_id || review?.episode_id || inc.episode_id || inc.operational_record?.episode_id;
  const artifact = review?.artifact || report?.artifact;
  const evidence = [...(review?.validation_evidence ?? []), ...(report?.diagnosis_evidence ?? [])];
  const diagnosis = review?.diagnosis || report?.diagnosis;

  const steps: ParsedOperationalRemediation['steps'] = [];
  if (diagnosis) steps.push({ label: 'Diagnosis', detail: String(diagnosis).replace(/_/g, ' '), status: 'ok' });
  (report?.attempts ?? []).forEach((a, i) =>
    steps.push({
      label: `${i + 1}. ${String(a.strategy || 'attempt').replace(/_/g, ' ')}`,
      detail: [...(a.actions ?? []), ...(a.evidence ?? [])].join('; '),
      status: a.success ? 'ok' : 'fail',
    }),
  );
  if (artifact?.source_path || artifact?.absolute_path || artifact?.expected_path) {
    steps.push({
      label: 'Artifact',
      detail: [artifact.source_path, artifact.absolute_path || artifact.expected_path].filter(Boolean).join(' → '),
      status: artifact.recovered || artifact.generated ? 'ok' : 'warn',
    });
  }
  if (evidence.length || validated) {
    steps.push({ label: 'Validation', detail: evidence.join('; ') || 'validated', status: validated ? 'ok' : 'pending' });
  }
  if (closedAt || resolution) {
    steps.push({
      label: 'Incident closed',
      detail: [closedAt ? `closed ${closedAt}` : null, resolution ? `resolution=${resolution}` : null].filter(Boolean).join(', '),
      status: 'ok',
    });
  }

  const exactChanges: ExactChange[] = [];
  if (artifact && (artifact.absolute_path || artifact.expected_path)) {
    exactChanges.push({
      kind: artifact.generated ? 'generate' : artifact.source_path ? 'copy' : 'restore',
      label: artifact.generated ? 'Generated file' : 'Placed file',
      detail: [artifact.source_path, artifact.absolute_path || artifact.expected_path].filter(Boolean).join(' → '),
    });
  }
  for (const a of report?.attempts ?? []) {
    for (const action of a.actions ?? []) exactChanges.push({ kind: 'action', label: 'Action', detail: action });
  }

  return {
    found: true,
    success,
    validated,
    summary: summary || (success ? 'Operational execution succeeded' : 'Operational execution recorded'),
    report,
    review,
    resolution,
    episodeId,
    closedAt,
    steps,
    exactChanges,
    validationChecks: evidence.map((label) => ({ label, pass: true })),
  };
}

/**
 * Whether operational recovery is idle, in flight, verified, failed, waiting
 * on a person, or closed — from the recorded execution and incident status.
 */
export function assessOperationalRecovery(
  inc?: AmsIncident | null,
  opts?: {
    decisionOutcome?: string | null;
    /** The operator's own verdict on the recovery. */
    humanAttestation?: 'approve_production' | 'reject_lab' | 'upstream_evidence' | 'decline_upstream' | null;
    upstreamEvidenceNote?: string | null;
    /** Operator clicked Rerun: show the recovery as in progress again. */
    forceReopen?: boolean;
  },
): OperationalRecoveryAssessment {
  const remediation = extractOperationalRemediation(inc);
  const incidentStatus = String(inc?.status || '').toLowerCase();
  const closed = !opts?.forceReopen && (Boolean(remediation.closedAt) || /^(closed|resolved)$/.test(incidentStatus));
  const attestation = opts?.humanAttestation;
  const report = remediation.report;
  const outcome = String(opts?.decisionOutcome || '').trim().toLowerCase();

  if (opts?.forceReopen) {
    return { status: 'running', message: 'Mission rerun — re-checking the recovery.', remediation };
  }
  if (attestation === 'decline_upstream' || attestation === 'reject_lab') {
    return { status: 'concluded_unresolved', message: 'Concluded without a verified recovery.', remediation };
  }
  if (closed) {
    return { status: 'closed', message: 'Operational event closed.', remediation };
  }
  if (attestation === 'approve_production' && remediation.found) {
    return { status: 'verified', message: 'Recovery confirmed by the operator.', remediation };
  }
  if (['duplicate', 'already_fixed', 'false_positive', 'no_action'].includes(outcome)) {
    return {
      status: 'verified',
      message: `Ops decision applied: ${outcome.replace(/_/g, ' ')} — no recovery run required.`,
      remediation,
    };
  }
  if (remediation.found) {
    if (report?.requires_human_approval || report?.requires_engineering) {
      return {
        status: 'needs_human',
        message: remediation.summary || 'The recovery needs a person — continue on the runbook.',
        remediation,
      };
    }
    if (remediation.success && remediation.validated) {
      return { status: 'verified', message: remediation.summary || 'Recovery verified.', remediation };
    }
    if (!remediation.success) {
      return {
        status: 'failed',
        message: remediation.summary || 'Operational execution finished without a verified recovery.',
        remediation,
      };
    }
    return { status: 'running', message: remediation.summary || 'Recovery ran; waiting for validation.', remediation };
  }
  const stage = String(inc?.mission?.current_stage || inc?.mission_stage || '').toLowerCase();
  const missionStatus = String(inc?.mission?.mission_status || '').toLowerCase();
  if (/operational|execution|validation/.test(stage) || missionStatus === 'active' || missionStatus === 'pending') {
    return { status: 'running', message: 'Operational recovery is running…', remediation };
  }
  return {
    status: 'idle',
    message: outcome === 'escalate' ? 'Escalated — follow the runbook or continue in Engineering.' : 'No operational recovery has run yet.',
    remediation,
  };
}
